import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import workflowAPI, { Workflow } from '../services/api';
import PageFooter from '../components/PageFooter';
import './SavedWorkflows.css';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : 'http://localhost:3001/api';

interface WorkflowTemplate {
  id: string;
  templateId: string;
  name: string;
  description: string;
  version?: string;
  category?: string;
  executable: boolean;
  steps?: any[];
}

const SavedWorkflows: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [viewingJson, setViewingJson] = useState<string | null>(null);
  const [executingWorkflow, setExecutingWorkflow] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<WorkflowTemplate | null>(null);
  const [executeParams, setExecuteParams] = useState({
    brdPath: 'requirements/brd_011_part_favorites.md',
    workspaceRoot: '/workflow-data',
    contextDirs: '',
    outputDir: 'sdlc-artifacts',
    gitRepoUrl: 'https://github.com/alokpandey/agent_sdlc.git',
    existingBrdPath: 'docs',
    existingArchPath: 'docs'
  });

  useEffect(() => {
    loadWorkflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = filter !== 'all' ? { status: filter } : {};
      const data = await workflowAPI.getWorkflows(filters);
      setWorkflows(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load workflows');
      console.error('Error loading workflows:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (workflowId: string) => {
    navigate(`/designer?id=${workflowId}`);
  };

  const handleDelete = async (workflowId: string, workflowName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${workflowName}"?`)) {
      return;
    }

    try {
      await workflowAPI.deleteWorkflow(workflowId);
      setWorkflows(workflows.filter(w => w.id !== workflowId));
    } catch (err: any) {
      alert(`Failed to delete workflow: ${err.message}`);
    }
  };

  const handleCreateNew = () => {
    navigate('/designer');
  };

  const handleShowTemplates = async () => {
    setShowTemplates(true);
    setLoadingTemplates(true);
    try {
      const data = await workflowAPI.getWorkflowTemplates();
      setTemplates(data);
    } catch (err: any) {
      alert(`Failed to load templates: ${err.message}`);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handlePreviewTemplate = (template: WorkflowTemplate) => {
    setPreviewTemplate(template);
  };

  const handleExecuteFromPreview = () => {
    if (previewTemplate) {
      setSelectedTemplate(previewTemplate);
      setPreviewTemplate(null);
      setShowTemplates(false);
    }
  };

  const handleEditInDesigner = (template: WorkflowTemplate) => {
    // Convert template to designer format
    const nodes = template.steps?.map((step, index) => {
      let nodeType = 'default';
      let label = step.name;

      if (step.type === 'start') {
        nodeType = 'input';
      } else if (step.type === 'end') {
        nodeType = 'output';
      } else if (step.type === 'loop') {
        // Special handling for loop nodes
        const loopConfig = step.config as any;
        if (loopConfig?.loopType === 'end') {
          label = `↩️ ${step.name}`;
        } else {
          label = `🔄 ${step.name}`;
        }
      }

      return {
        id: step.id,
        type: nodeType,
        position: { x: 250, y: 100 + (index * 120) },
        data: {
          label,
          stepType: step.type,
          agentId: (step.config as any)?.agentId || '',
          agentName: (step.config as any)?.agentName || '',
          approvalMessage: (step.config as any)?.message || '',
          timeout: (step.config as any)?.timeout || '24h',
          loopType: (step.config as any)?.loopType || '',
          iterateOver: (step.config as any)?.iterateOver || ''
        }
      };
    }) || [];

    const edges = template.steps?.slice(0, -1).map((step, index) => ({
      id: `e${index}`,
      source: template.steps![index].id,
      target: template.steps![index + 1].id,
      type: 'smoothstep'
    })) || [];

    const designerWorkflow = {
      name: template.name,
      description: template.description,
      nodes,
      edges
    };

    // Store in sessionStorage so designer can load it
    sessionStorage.setItem('loadedWorkflow', JSON.stringify(designerWorkflow));
    sessionStorage.setItem('loadedFromTemplate', 'true');
    sessionStorage.setItem('templateId', template.templateId);

    // Navigate to designer
    navigate('/designer');
  };

  const handleSelectTemplate = (template: WorkflowTemplate) => {
    setSelectedTemplate(template);
    setShowTemplates(false);
  };

  const handleExecuteTemplate = async () => {
    if (!selectedTemplate) return;

    // Check if this is the demo-only Complete SDLC workflow
    if (selectedTemplate.templateId === 'complete-sdlc') {
      alert('ℹ️ Demonstration Only\n\nThis workflow is for demonstration purposes only and is not configured in the backend to trigger an actual workflow execution.\n\nPlease use one of the other available templates:\n• SDLC Workflow - Epic Generation with Approval\n• Code Implementation & Unit Tests\n• SonarQube Security Scan & Auto-Remediation');
      setSelectedTemplate(null);
      return;
    }

    try {
      // Get auth token from localStorage
      const accessToken = localStorage.getItem('accessToken');
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };

      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`${API_BASE_URL}/workflows/templates/${selectedTemplate.templateId}/execute`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(executeParams)
      });

      const data = await response.json();

      // Handle 401 Unauthorized
      if (response.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('user');
        alert('Session expired. Please log in again.');
        navigate('/login');
        return;
      }

      if (data.success) {
        // Close the template modal
        setSelectedTemplate(null);

        // Navigate to Executions tab immediately
        navigate('/executions');

        // Show success message after navigation
        setTimeout(() => {
          alert(`✅ Template "${selectedTemplate.name}" started!\n\nWorkflow ID: ${data.data.workflowId}\nExecution ID: ${data.data.executionId}`);
        }, 100);
      } else {
        alert(`Failed to execute template: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Failed to execute template: ${err.message}`);
    }
  };

  const handleCancelTemplateExecution = () => {
    setSelectedTemplate(null);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const handleViewJson = (workflow: Workflow) => {
    setViewingJson(workflow.id!);
  };

  const handleCloseJson = () => {
    setViewingJson(null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('JSON copied to clipboard!');
  };

  const handleExecute = (workflowId: string) => {
    setExecutingWorkflow(workflowId);
  };

  const handleExecuteSubmit = async (workflowId: string) => {
    try {
      const result = await workflowAPI.executeWorkflow(workflowId, executeParams);

      // Close the execution modal
      setExecutingWorkflow(null);

      // Navigate to Executions tab immediately
      navigate('/executions');

      // Show success message after navigation
      setTimeout(() => {
        alert(`Workflow execution started successfully!\n\nExecution ID: ${result.execution_id}\nTemporal Workflow ID: ${result.temporal_workflow_id}`);
      }, 100);
    } catch (err: any) {
      alert(`Failed to execute workflow: ${err.message}`);
    }
  };

  const handleCancelExecute = () => {
    setExecutingWorkflow(null);
  };

  return (
    <div className="saved-workflows">
      <div className="workflows-header">
        <div>
          <h1>Saved Workflows</h1>
          <p>Manage your workflow designs</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={handleShowTemplates}>
            📋 Load Template
          </button>
          <button className="btn-primary" onClick={handleCreateNew}>
            + Create New Workflow
          </button>
        </div>
      </div>

      <div className="workflows-filters">
        <button 
          className={filter === 'all' ? 'filter-btn active' : 'filter-btn'}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button 
          className={filter === 'draft' ? 'filter-btn active' : 'filter-btn'}
          onClick={() => setFilter('draft')}
        >
          Drafts
        </button>
        <button 
          className={filter === 'active' ? 'filter-btn active' : 'filter-btn'}
          onClick={() => setFilter('active')}
        >
          Active
        </button>
      </div>

      <div className="workflows-content">
        {loading && <div className="loading">Loading workflows...</div>}
        
        {error && (
          <div className="error-message">
            <p>⚠️ {error}</p>
            <button onClick={loadWorkflows}>Retry</button>
          </div>
        )}

        {!loading && !error && workflows.length === 0 && (
          <div className="empty-state">
            <h3>No workflows found</h3>
            <p>Create your first workflow or load a template to get started</p>
            <button className="btn-primary" onClick={handleCreateNew}>
              Create Workflow
            </button>
          </div>
        )}

        {!loading && !error && workflows.length > 0 && (
          <div className="workflows-grid">
            {workflows.map((workflow) => (
              <div key={workflow.id} className="workflow-card">
                <div className="workflow-card-header">
                  <h3>{workflow.name}</h3>
                  <span className={`status-badge ${workflow.status}`}>
                    {workflow.status}
                  </span>
                </div>
                <p className="workflow-description">
                  {workflow.description || 'No description'}
                </p>
                <div className="workflow-meta">
                  <div className="meta-item">
                    <span className="meta-label">Nodes:</span>
                    <span className="meta-value">{workflow.nodes?.length || 0}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Version:</span>
                    <span className="meta-value">v{workflow.version || 1}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Updated:</span>
                    <span className="meta-value">{formatDate(workflow.updated_at)}</span>
                  </div>
                </div>
                <div className="workflow-actions">
                  {/* Only show Execute button for executors and admins */}
                  {(user?.role === 'admin' || user?.role === 'workflow_executor') && (
                    <button
                      className="btn-primary"
                      onClick={() => handleExecute(workflow.id!)}
                      title="Execute this workflow"
                    >
                      ▶ Execute
                    </button>
                  )}
                  <button
                    className="btn-secondary"
                    onClick={() => handleEdit(workflow.id!)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => handleViewJson(workflow)}
                  >
                    View JSON
                  </button>
                  {/* Only show Delete button for creators and admins */}
                  {(user?.role === 'admin' || user?.role === 'workflow_creator') && (
                    <button
                      className="btn-danger"
                      onClick={() => handleDelete(workflow.id!, workflow.name)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {viewingJson && (
        <div className="json-modal-overlay" onClick={handleCloseJson}>
          <div className="json-modal" onClick={(e) => e.stopPropagation()}>
            <div className="json-modal-header">
              <h2>Workflow JSON</h2>
              <button className="close-btn" onClick={handleCloseJson}>×</button>
            </div>
            <div className="json-modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  const workflow = workflows.find(w => w.id === viewingJson);
                  if (workflow) copyToClipboard(JSON.stringify(workflow, null, 2));
                }}
              >
                Copy to Clipboard
              </button>
            </div>
            <pre className="json-content">
              {JSON.stringify(
                workflows.find(w => w.id === viewingJson),
                null,
                2
              )}
            </pre>
          </div>
        </div>
      )}

      {showTemplates && (
        <div className="json-modal-overlay" onClick={() => setShowTemplates(false)}>
          <div className="json-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="json-modal-header">
              <h2>Select Workflow Template</h2>
              <button className="close-btn" onClick={() => setShowTemplates(false)}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              {loadingTemplates ? (
                <p>Loading templates...</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '15px' }}>
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '15px',
                        transition: 'all 0.2s',
                        backgroundColor: '#fff'
                      }}
                    >
                      <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>
                        {template.name}
                      </h3>
                      <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#666', lineHeight: '1.4' }}>
                        {template.description}
                      </p>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
                        {template.category && (
                          <span style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            borderRadius: '4px'
                          }}>
                            {template.category}
                          </span>
                        )}
                        {template.version && (
                          <span style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            backgroundColor: '#f5f5f5',
                            color: '#666',
                            borderRadius: '4px'
                          }}>
                            v{template.version}
                          </span>
                        )}
                        {template.steps && (
                          <span style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            backgroundColor: '#e8f5e9',
                            color: '#2e7d32',
                            borderRadius: '4px'
                          }}>
                            {template.steps.length} steps
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreviewTemplate(template);
                            }}
                            style={{ flex: 1, padding: '8px 12px', fontSize: '13px' }}
                          >
                            👁️ Preview
                          </button>
                          <button
                            className="btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectTemplate(template);
                            }}
                            style={{ flex: 1, padding: '8px 12px', fontSize: '13px' }}
                          >
                            ▶ Execute
                          </button>
                        </div>
                        <button
                          className="btn-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditInDesigner(template);
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            fontSize: '13px',
                            backgroundColor: '#2196F3',
                            color: 'white',
                            border: 'none'
                          }}
                        >
                          ✏️ Edit in Designer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {previewTemplate && (
        <div className="json-modal-overlay" onClick={() => setPreviewTemplate(null)}>
          <div className="json-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="json-modal-header">
              <h2>📋 Template Preview: {previewTemplate.name}</h2>
              <button className="close-btn" onClick={() => setPreviewTemplate(null)}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
                {previewTemplate.description}
              </p>

              {/* Workflow Steps Visualization */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', marginBottom: '15px', color: '#333' }}>Workflow Steps:</h3>
                {previewTemplate.steps && previewTemplate.steps.map((step, index) => (
                  <div key={step.id} style={{ marginBottom: '15px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '15px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: '#f9f9f9'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor:
                          step.type === 'agent' ? '#4CAF50' :
                          step.type === 'approval' ? '#FF9800' :
                          step.type === 'loop' ? '#2196F3' :
                          step.type === 'end' ? '#9E9E9E' : '#9E9E9E',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        marginRight: '15px',
                        flexShrink: 0
                      }}>
                        {step.type === 'end' ? '✓' : step.type === 'loop' ? '🔄' : index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                          {step.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Type: <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor:
                              step.type === 'agent' ? '#e8f5e9' :
                              step.type === 'approval' ? '#fff3e0' :
                              step.type === 'loop' ? '#e3f2fd' :
                              step.type === 'end' ? '#f5f5f5' : '#f5f5f5',
                            color:
                              step.type === 'agent' ? '#2e7d32' :
                              step.type === 'approval' ? '#e65100' :
                              step.type === 'loop' ? '#1976d2' :
                              step.type === 'end' ? '#666' : '#666'
                          }}>
                            {step.type === 'agent' ? '🤖 Agent' :
                             step.type === 'approval' ? '✋ Manual Approval' :
                             step.type === 'loop' ? '🔄 Loop' :
                             step.type === 'end' ? '🏁 End' : '⚙️ Custom'}
                          </span>
                        </div>
                      </div>
                    </div>
                    {index < (previewTemplate.steps?.length || 0) - 1 && (
                      <div style={{
                        width: '2px',
                        height: '20px',
                        backgroundColor: '#e0e0e0',
                        margin: '5px 0 5px 20px'
                      }} />
                    )}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', borderTop: '1px solid #e0e0e0', paddingTop: '15px' }}>
                <button
                  className="btn-secondary"
                  onClick={() => {
                    if (previewTemplate) {
                      handleEditInDesigner(previewTemplate);
                    }
                  }}
                  style={{ backgroundColor: '#2196F3', color: 'white' }}
                >
                  ✏️ Edit in Designer
                </button>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button className="btn-secondary" onClick={() => setPreviewTemplate(null)}>
                    Close
                  </button>
                  {/* Only show Execute button for executors and admins */}
                  {(user?.role === 'admin' || user?.role === 'workflow_executor') && (
                    <button className="btn-primary" onClick={handleExecuteFromPreview}>
                      ▶ Execute This Template
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedTemplate && (
        <div className="json-modal-overlay" onClick={handleCancelTemplateExecution}>
          <div className="json-modal" onClick={(e) => e.stopPropagation()}>
            <div className="json-modal-header">
              <h2>Execute Template: {selectedTemplate.name}</h2>
              <button className="close-btn" onClick={handleCancelTemplateExecution}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                {selectedTemplate.description}
              </p>

              {/* Show minimal dialog for SonarQube - no BRD/epic/story needed */}
              {selectedTemplate.templateId === 'sonar-qube-integration' ? (
                <>
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#f0f7ff',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid #3b82f6'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#1e40af' }}>🔒 Security Scan Configuration</h4>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Workspace:</strong> /workflow-data
                    </p>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Repository:</strong> https://github.com/alokpandey/agent_sdlc.git
                    </p>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Scan Path:</strong> . (repository root)
                    </p>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Project Key:</strong> alokpandey_agent_sdlc
                    </p>
                    <p style={{ margin: '10px 0 0 0', fontSize: '13px', color: '#666' }}>
                      This workflow will scan your Java/JavaScript multi-module project for security vulnerabilities using SonarCloud and automatically create remediation tasks.
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button className="btn-secondary" onClick={handleCancelTemplateExecution}>
                      Cancel
                    </button>
                    <button className="btn-primary" onClick={handleExecuteTemplate}>
                      ▶ Start Security Scan
                    </button>
                  </div>
                </>
              ) : (
                /* Show full dialog for other templates that need BRD/epic/story parameters */
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      BRD Path:
                    </label>
                    <input
                      type="text"
                      value={executeParams.brdPath}
                      onChange={(e) => setExecuteParams({ ...executeParams, brdPath: e.target.value })}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      placeholder="requirements/brd_011_part_favorites.md"
                    />
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Workspace Root:
                    </label>
                    <input
                      type="text"
                      value={executeParams.workspaceRoot}
                      onChange={(e) => setExecuteParams({ ...executeParams, workspaceRoot: e.target.value })}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      placeholder="/Users/alokpandey/agentic_sdlc_workflow"
                    />
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Context Directories (optional):
                    </label>
                    <input
                      type="text"
                      value={executeParams.contextDirs}
                      onChange={(e) => setExecuteParams({ ...executeParams, contextDirs: e.target.value })}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      placeholder="src,lib"
                    />
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Output Directory:
                    </label>
                    <input
                      type="text"
                      value={executeParams.outputDir}
                      onChange={(e) => setExecuteParams({ ...executeParams, outputDir: e.target.value })}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      placeholder="sdlc-artifacts"
                    />
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      GitHub Repository URL:
                    </label>
                    <input
                      type="text"
                      value={executeParams.gitRepoUrl}
                      disabled
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        backgroundColor: '#f5f5f5',
                        color: '#666',
                        cursor: 'not-allowed'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Existing BRD/Docs Path (in Git Repo):
                    </label>
                    <input
                      type="text"
                      value={executeParams.existingBrdPath}
                      disabled
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        backgroundColor: '#f5f5f5',
                        color: '#666',
                        cursor: 'not-allowed'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Existing Architecture/Docs Path (in Git Repo):
                    </label>
                    <input
                      type="text"
                      value={executeParams.existingArchPath}
                      disabled
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        backgroundColor: '#f5f5f5',
                        color: '#666',
                        cursor: 'not-allowed'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button className="btn-secondary" onClick={handleCancelTemplateExecution}>
                      Cancel
                    </button>
                    <button className="btn-primary" onClick={handleExecuteTemplate}>
                      ▶ Start Execution
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {executingWorkflow && (
        <div className="json-modal-overlay" onClick={handleCancelExecute}>
          <div className="json-modal" onClick={(e) => e.stopPropagation()}>
            <div className="json-modal-header">
              <h2>Execute Workflow</h2>
              <button className="close-btn" onClick={handleCancelExecute}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '20px' }}>
                Configure execution parameters for: <strong>{workflows.find(w => w.id === executingWorkflow)?.name}</strong>
              </p>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  BRD Path:
                </label>
                <input
                  type="text"
                  value={executeParams.brdPath}
                  onChange={(e) => setExecuteParams({ ...executeParams, brdPath: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  placeholder="requirements/brd_011_part_favorites.md"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Workspace Root:
                </label>
                <input
                  type="text"
                  value={executeParams.workspaceRoot}
                  onChange={(e) => setExecuteParams({ ...executeParams, workspaceRoot: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  placeholder="/Users/alokpandey/agentic_sdlc_workflow"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Context Directories (optional):
                </label>
                <input
                  type="text"
                  value={executeParams.contextDirs}
                  onChange={(e) => setExecuteParams({ ...executeParams, contextDirs: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  placeholder="src,lib"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Output Directory:
                </label>
                <input
                  type="text"
                  value={executeParams.outputDir}
                  onChange={(e) => setExecuteParams({ ...executeParams, outputDir: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  placeholder="sdlc-artifacts"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  GitHub Repository URL:
                </label>
                <input
                  type="text"
                  value={executeParams.gitRepoUrl}
                  disabled
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    cursor: 'not-allowed'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Existing BRD Document Path:
                </label>
                <input
                  type="text"
                  value={executeParams.existingBrdPath}
                  disabled
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    cursor: 'not-allowed'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Existing Architecture Document Path:
                </label>
                <input
                  type="text"
                  value={executeParams.existingArchPath}
                  disabled
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    cursor: 'not-allowed'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button className="btn-secondary" onClick={handleCancelExecute}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={() => handleExecuteSubmit(executingWorkflow)}
                >
                  ▶ Start Execution
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <PageFooter />
    </div>
  );
};

export default SavedWorkflows;

